// v10/src/components/DesktopBackground.js
// Fondo aeronáutico — solo desktop (≥1024px).
import React from 'react';

const DesktopBackground = ({ variant = 'default' }) => {
  const dark = variant === 'dark';
  const blue = dark ? '#7ba4f7' : '#3b82f6';
  const purple = dark ? '#a78bfa' : '#7c3aed';

  return (
    <>
      <style>{`
        .condor-bg { display: none !important; }
        @media (min-width: 1024px) {
          .condor-bg {
            display: block !important;
            position: fixed; inset: 0; z-index: 0;
            pointer-events: none; overflow: hidden;
          }
        }
      `}</style>

      <div className="condor-bg">

        {/* ── Gradiente base ─────────────────────────────── */}
        <div style={{
          position: 'absolute', inset: 0,
          background: dark
            ? 'radial-gradient(ellipse 160% 90% at 50% -15%, rgba(40,80,200,0.38) 0%, transparent 55%), radial-gradient(ellipse 90% 65% at 90% 100%, rgba(120,60,230,0.22) 0%, transparent 50%)'
            : 'radial-gradient(ellipse 160% 90% at 50% -15%, rgba(59,130,246,0.18) 0%, transparent 55%), radial-gradient(ellipse 90% 65% at 90% 100%, rgba(139,92,246,0.14) 0%, transparent 50%)',
        }} />

        {/* ── Grid ────────────────────────────────────────── */}
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: `linear-gradient(${dark?'rgba(130,170,255,0.1)':'rgba(59,130,246,0.09)'} 1px, transparent 1px), linear-gradient(90deg, ${dark?'rgba(130,170,255,0.1)':'rgba(59,130,246,0.09)'} 1px, transparent 1px)`,
          backgroundSize: '56px 56px',
        }} />

        {/* ── Pista en perspectiva ────────────────────────── */}
        <svg style={{ position:'absolute', bottom:0, left:'50%', transform:'translateX(-50%)', width:'860px', height:'420px', opacity: dark?0.2:0.16 }}
          viewBox="0 0 860 420" fill="none">
          {/* Área de pista rellena */}
          <polygon points="430,0 130,420 730,420" fill={blue} opacity="0.04"/>
          {/* Bordes principales */}
          <line x1="430" y1="0" x2="130" y2="420" stroke={blue} strokeWidth="2.5"/>
          <line x1="430" y1="0" x2="730" y2="420" stroke={blue} strokeWidth="2.5"/>
          {/* Línea central discontinua */}
          {Array.from({length:12},(_,i)=>{
            const y1=i*36, y2=y1+20;
            const f1=y1/420, f2=y2/420;
            const lx1=430-(430-130)*f1, rx1=430+(730-430)*f1;
            const lx2=430-(430-130)*f2, rx2=430+(730-430)*f2;
            return <line key={i} x1={(lx1+rx1)/2} y1={y1} x2={(lx2+rx2)/2} y2={y2} stroke={blue} strokeWidth="1.5" opacity="0.85"/>;
          })}
          {/* Marcas transversales */}
          {[70,150,240,330,400].map((y,i)=>{
            const f=y/420; const lx=430-(430-130)*f; const rx=430+(730-430)*f;
            return <line key={i} x1={lx} y1={y} x2={rx} y2={y} stroke={blue} strokeWidth="1.2" opacity="0.6"/>;
          })}
          {/* Umbral */}
          <line x1="145" y1="412" x2="715" y2="412" stroke={blue} strokeWidth="2.5"/>
          {Array.from({length:14},(_,i)=>(
            <rect key={i} x={165+i*40} y={397} width="22" height="13" fill={blue} opacity="0.55" rx="2"/>
          ))}
        </svg>

        {/* ── Rosa de los vientos — grande y centrada ─────── */}
        <svg style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', width:'640px', height:'640px', opacity: dark?0.13:0.1 }}
          viewBox="0 0 640 640" fill="none">
          {/* Círculos */}
          {[55,115,190,270].map((r,i)=>(
            <circle key={i} cx="320" cy="320" r={r} stroke={blue} strokeWidth={i===3?2:1.2}/>
          ))}
          {/* 16 radios */}
          {Array.from({length:16},(_,i)=>{
            const rad=i*22.5*Math.PI/180;
            return <line key={i} x1="320" y1="320" x2={320+275*Math.sin(rad)} y2={320-275*Math.cos(rad)} stroke={blue} strokeWidth={i%4===0?1.8:0.8}/>;
          })}
          {/* Puntas cardinales */}
          {[
            {d:'M320,30 L332,82 L320,68 L308,82 Z',tx:316,ty:20,l:'N'},
            {d:'M320,610 L332,558 L320,572 L308,558 Z',tx:316,ty:632,l:'S'},
            {d:'M610,320 L558,308 L572,320 L558,332 Z',tx:622,ty:325,l:'E'},
            {d:'M30,320 L82,308 L68,320 L82,332 Z',tx:16,ty:325,l:'W'},
          ].map((p,i)=>(
            <g key={i}>
              <path d={p.d} fill={blue}/>
              <text x={p.tx} y={p.ty} fontSize="18" fontWeight="900" fill={blue} textAnchor="middle" fontFamily="monospace">{p.l}</text>
            </g>
          ))}
          {/* NE NO SE SO */}
          {[45,135,225,315].map((deg,i)=>{
            const rad=deg*Math.PI/180;
            const x=320+295*Math.sin(rad), y=320-295*Math.cos(rad);
            const labels=['NE','SE','SO','NO'];
            return <text key={i} x={x} y={y} fontSize="11" fontWeight="700" fill={blue} textAnchor="middle" dominantBaseline="middle" fontFamily="monospace" opacity="0.7">{labels[i]}</text>;
          })}
          {/* Marcas de grado cada 10° */}
          {Array.from({length:36},(_,i)=>{
            const rad=i*10*Math.PI/180, r1=265, r2=i%3===0?248:256;
            return <line key={i} x1={320+r1*Math.sin(rad)} y1={320-r1*Math.cos(rad)} x2={320+r2*Math.sin(rad)} y2={320-r2*Math.cos(rad)} stroke={blue} strokeWidth="1"/>;
          })}
        </svg>

        {/* ── Escala altitud — izquierda ──────────────────── */}
        <svg style={{ position:'absolute', left:'20px', top:'50%', transform:'translateY(-50%)', width:'54px', height:'480px', opacity: dark?0.2:0.16 }}
          viewBox="0 0 54 480" fill="none">
          <line x1="46" y1="0" x2="46" y2="480" stroke={blue} strokeWidth="1.5"/>
          {Array.from({length:25},(_,i)=>{
            const y=i*20, main=i%5===0, mid=i%2===0, len=main?22:mid?12:6;
            return <g key={i}>
              <line x1={46-len} y1={y} x2="46" y2={y} stroke={blue} strokeWidth={main?1.5:0.8}/>
              {main && <text x={42-len} y={y+4} fontSize="9.5" fill={blue} textAnchor="end" fontFamily="monospace">{(12-i/2)*1000}</text>}
            </g>;
          })}
          <text x="10" y="245" fontSize="9" fill={blue} fontFamily="monospace" transform="rotate(-90,10,245)" textAnchor="middle" opacity="0.6">ALT ft</text>
        </svg>

        {/* ── Escala rumbo — superior ─────────────────────── */}
        <svg style={{ position:'absolute', top:'16px', left:'50%', transform:'translateX(-50%)', width:'700px', height:'46px', opacity: dark?0.2:0.16 }}
          viewBox="0 0 700 46" fill="none">
          <line x1="0" y1="10" x2="700" y2="10" stroke={blue} strokeWidth="1.5"/>
          {Array.from({length:36},(_,i)=>{
            const x=i*20, main=i%5===0, mid=i%2===0, len=main?20:mid?12:6;
            return <g key={i}>
              <line x1={x} y1="10" x2={x} y2={10+len} stroke={blue} strokeWidth={main?1.5:0.8}/>
              {main && <text x={x} y={10+len+12} fontSize="9.5" fill={blue} textAnchor="middle" fontFamily="monospace">{i*10}°</text>}
            </g>;
          })}
          {/* Triángulo posición actual */}
          <polygon points="350,3 342,16 358,16" fill={blue} opacity="0.85"/>
          <line x1="350" y1="16" x2="350" y2="46" stroke={blue} strokeWidth="1" strokeDasharray="3 3" opacity="0.5"/>
        </svg>

        {/* ── Indicador de velocímetro / VSI — derecha ───── */}
        <svg style={{ position:'absolute', right:'28px', top:'50%', transform:'translateY(-50%)', width:'60px', height:'440px', opacity: dark?0.18:0.14 }}
          viewBox="0 0 60 440" fill="none">
          <line x1="14" y1="0" x2="14" y2="440" stroke={purple} strokeWidth="1.5"/>
          {Array.from({length:23},(_,i)=>{
            const y=i*20, main=i%5===0, mid=i%2===0, len=main?22:mid?12:6;
            return <g key={i}>
              <line x1="14" y1={y} x2={14+len} y2={y} stroke={purple} strokeWidth={main?1.5:0.8}/>
              {main && <text x={18+len} y={y+4} fontSize="9.5" fill={purple} fontFamily="monospace">{(11-i/2)*10}</text>}
            </g>;
          })}
          <text x="46" y="245" fontSize="9" fill={purple} fontFamily="monospace" transform="rotate(90,46,245)" textAnchor="middle" opacity="0.6">SPD kt</text>
        </svg>

        {/* ── Silueta avión centrada ──────────────────────── */}
        <div style={{ position:'absolute', bottom:'230px', right:'100px', opacity: dark?0.1:0.08, transform:'rotate(-20deg)' }}>
          <svg width="260" height="260" viewBox="0 0 260 260" fill={blue}>
            <ellipse cx="130" cy="130" rx="90" ry="15"/>
            <path d="M108,130 L20,168 L48,130 L20,92 Z"/>
            <path d="M152,130 L240,168 L212,130 L240,92 Z"/>
            <path d="M46,130 L18,144 L24,130 L18,116 Z"/>
            <path d="M214,130 L242,144 L236,130 L242,116 Z"/>
            <path d="M48,130 L38,96 L52,130 Z"/>
          </svg>
        </div>

        {/* ── Acentos de esquina ──────────────────────────── */}
        <div style={{ position:'absolute', top:0, left:0, width:'380px', height:'380px',
          background: dark
            ? 'radial-gradient(circle at 0% 0%, rgba(59,130,246,0.18) 0%, transparent 65%)'
            : 'radial-gradient(circle at 0% 0%, rgba(59,130,246,0.12) 0%, transparent 65%)' }} />
        <div style={{ position:'absolute', bottom:0, right:0, width:'420px', height:'420px',
          background: dark
            ? 'radial-gradient(circle at 100% 100%, rgba(139,92,246,0.18) 0%, transparent 65%)'
            : 'radial-gradient(circle at 100% 100%, rgba(139,92,246,0.12) 0%, transparent 65%)' }} />

        {/* ── Anillos HUD ─────────────────────────────────── */}
        <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', width:'1100px', height:'1100px', borderRadius:'50%', border:`1px solid ${dark?'rgba(99,130,246,0.09)':'rgba(59,130,246,0.07)'}` }} />
        <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', width:'1600px', height:'1600px', borderRadius:'50%', border:`1px solid ${dark?'rgba(99,130,246,0.05)':'rgba(59,130,246,0.04)'}` }} />
      </div>
    </>
  );
};

export default DesktopBackground;
